import { useState, useEffect, useMemo } from 'react'

export type TableDensity = 'compact' | 'normal' | 'comfortable'

export interface ColumnSettings {
  visible: boolean
  width?: string
  order: number
}

export interface TableCustomizationSettings {
  density: TableDensity
  columns: Record<string, ColumnSettings>
}

interface UseTableCustomizationOptions {
  storageKey: string
  defaultColumns: string[]
  defaultDensity?: TableDensity
}

export function useTableCustomization({
  storageKey,
  defaultColumns,
  defaultDensity = 'normal'
}: UseTableCustomizationOptions) {
  // Estado das configurações
  const [settings, setSettings] = useState<TableCustomizationSettings>(() => {
    // Tentar carregar do localStorage
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(storageKey)
        if (stored) {
          return JSON.parse(stored)
        }
      } catch (error) {
        console.error('Erro ao carregar configurações da tabela:', error)
      }
    }

    // Configuração padrão
    return {
      density: defaultDensity,
      columns: defaultColumns.reduce((acc, col, index) => {
        acc[col] = { visible: true, order: index }
        return acc
      }, {} as Record<string, ColumnSettings>)
    }
  })

  // Persistir no localStorage quando mudar
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem(storageKey, JSON.stringify(settings))
      } catch (error) {
        console.error('Erro ao salvar configurações da tabela:', error)
      }
    }
  }, [settings, storageKey])

  // Colunas ordenadas e visíveis
  const visibleColumns = useMemo(() => {
    return Object.entries(settings.columns)
      .filter(([, config]) => config.visible)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)
  }, [settings.columns])

  // Funções de manipulação
  const toggleColumn = (columnKey: string) => {
    setSettings(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        [columnKey]: {
          ...prev.columns[columnKey],
          visible: !prev.columns[columnKey]?.visible
        }
      }
    }))
  }

  const setColumnWidth = (columnKey: string, width: string) => {
    setSettings(prev => ({
      ...prev,
      columns: {
        ...prev.columns,
        [columnKey]: {
          ...prev.columns[columnKey],
          width
        }
      }
    }))
  }

  const reorderColumns = (fromIndex: number, toIndex: number) => {
    const columnKeys = Object.keys(settings.columns).sort(
      (a, b) => settings.columns[a].order - settings.columns[b].order
    )

    const [movedKey] = columnKeys.splice(fromIndex, 1)
    columnKeys.splice(toIndex, 0, movedKey)

    setSettings(prev => ({
      ...prev,
      columns: columnKeys.reduce((acc, key, index) => {
        acc[key] = {
          ...prev.columns[key],
          order: index
        }
        return acc
      }, {} as Record<string, ColumnSettings>)
    }))
  }

  const setDensity = (density: TableDensity) => {
    setSettings(prev => ({ ...prev, density }))
  }

  const resetToDefaults = () => {
    setSettings({
      density: defaultDensity,
      columns: defaultColumns.reduce((acc, col, index) => {
        acc[col] = { visible: true, order: index }
        return acc
      }, {} as Record<string, ColumnSettings>)
    })
  }

  const getDensityClasses = () => {
    switch (settings.density) {
      case 'compact':
        return {
          headerPadding: 'px-4 py-2',
          cellPadding: 'px-4 py-2',
          textSize: 'text-xs'
        }
      case 'comfortable':
        return {
          headerPadding: 'px-8 py-4',
          cellPadding: 'px-8 py-5',
          textSize: 'text-base'
        }
      default: // normal
        return {
          headerPadding: 'px-6 py-3',
          cellPadding: 'px-6 py-4',
          textSize: 'text-sm'
        }
    }
  }

  return {
    settings,
    visibleColumns,
    toggleColumn,
    setColumnWidth,
    reorderColumns,
    setDensity,
    resetToDefaults,
    getDensityClasses
  }
}

// Controller type exported for optional external control of the customization UI
export type TableCustomizationController = ReturnType<typeof useTableCustomization>
